import React, { type FC, useEffect, useCallback, useMemo } from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { DataHooks } from '@app/constants';
import { useConsentPolicy, useBiLogger } from '@app/hooks';
import { useWidgetSelector } from '@app/hooks/redux-hooks';
import { type RootState } from '@app/store/widget/widget-store';
import { ADUNIT_SIZE } from '@app/types/dimensions';
import {
  getAdUnitId,
  getAdUnitPublisherId,
  getDefaultAdDimensions,
  getDimensionsFromSize,
  getSizeTypeFromSize,
} from '@app/utils';
import AdSenseMetaAndScript from './adsense-meta-and-script';

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

const LiveSiteContent: FC = () => {
  const { adUnit } = useWidgetSelector((state: RootState) => state.widget);
  const { isMobile } = useEnvironment();
  const { isAdvertisingAllowed } = useConsentPolicy();
  const { biLiveSite } = useBiLogger();

  const adClickHandler = useCallback(() => {
    biLiveSite.adClick();
  }, [biLiveSite]);

  useEffect(() => {
    biLiveSite.adView();
  }, [biLiveSite]);

  // Initialize ad unit after component mounts and AdSense script is ready
  useEffect(() => {
    if (!adUnit) {
      return;
    }

    // Small delay to ensure all widgets have rendered their ad units
    const timer = setTimeout(() => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (error) {
        console.error('AdSense ad unit initialization failed:', error);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [adUnit]);

  const adDimensions = useMemo(() => {
    const defaultDimensions = getDefaultAdDimensions(isMobile);
    if (isMobile) {
      return {
        width: `${defaultDimensions.width}px`,
        height: `${defaultDimensions.height}px`,
      };
    }

    const sizeType = getSizeTypeFromSize(adUnit?.contentAdsSettings?.size);
    const dimensions = getDimensionsFromSize(adUnit?.contentAdsSettings?.size);

    return sizeType === ADUNIT_SIZE.FIXED
      ? {
          width: `${dimensions.width}px` || `${defaultDimensions.width}px`,
          height: `${dimensions.height}px` || `${defaultDimensions.height}px`,
        }
      : { width: '100%', height: '100%' };
  }, [isMobile, adUnit]);

  if (!adUnit || !isAdvertisingAllowed) {
    return <></>;
  }

  return (
    <>
      <AdSenseMetaAndScript />
      {Object.keys(adUnit).length > 0 && (
        <div
          data-hook={DataHooks.adsenseContainer}
          tabIndex={0}
          role="button"
          onClick={adClickHandler}
          style={{
            display: 'inline-block',
          }}
        >
          <ins
            data-hook={DataHooks.adsbygoogleIns}
            className="adsbygoogle"
            style={{
              position: 'absolute',
              width: adDimensions.width,
              height: adDimensions.height,
            }}
            data-ad-client={getAdUnitPublisherId(adUnit)}
            data-ad-slot={getAdUnitId(adUnit)}
          />
        </div>
      )}
    </>
  );
};

export default LiveSiteContent;
